import React, { useMemo } from 'react';
import {
    Box,
    Button,
    IconButton,
    Typography,
    Checkbox,
    FormControlLabel,
    Tooltip
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Receipt as ReceiptIcon,
    ViewList as ViewListIcon,
    Pending as PendingIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CommonTableControls, {
    CommonIconButton,
    CommonSelectionDisplay
} from '../../../../components/common/CommonTableControls';
import AllMyToggle from '../../../../components/common/AllMyToggle';
import {
    ColumnSelectionPanel,
    ColumnSelectionGrid,
    ColumnSelectionItem
} from '../styles';
import { getBasicColumns } from '../constants/invoiceConstants';

const TableControls = ({
    t,
    showMyOrderOnly,
    setShowMyOrderOnly,
    hideInvoicedRows,
    setHideInvoicedRows,
    toggleColumnPanel,
    columnPanelOpen,
    columnsToUse,
    checkedColumns,
    handleCheckColumn,
    selectAllColumns,
    unselectAllColumns,
    selectedItems,
    onInvoiceIssuance,
    onDebitNoteExport,
    summary,
    hasSearched = false,
    loadedCount = 0,
    totalCount = 0,
    hasMore = false
}) => {
    const { i18n } = useTranslation();
    const englishLabelById = useMemo(() => {
        const tEn = i18n.getFixedT('en');
        const map = {};
        getBasicColumns(tEn).forEach((col) => {
            map[col.id] = col.label;
        });
        return map;
    }, [i18n]);
    const centerContent = summary ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, whiteSpace: 'nowrap' }}>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {t('common.totalItems')}&nbsp;
                    <Box component="span" sx={{ fontWeight: 700, color: '#0f172a' }}>{summary.count}</Box>
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    {t('common.totalQty', { defaultValue: 'TOTAL QTY' })}&nbsp;
                    <Box component="span" sx={{ fontWeight: 700, color: '#1976d2' }}>{summary.totalQty}</Box>
                </Typography>
            </Box>
            <Box sx={{ minWidth: 220 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderBottom: '1px solid #e2e8f0',
                    pb: 0.4,
                    mb: 0.4
                }}>
                    <Typography sx={{
                        fontSize: '0.68rem',
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}>
                        {t('common.totalAmount', { defaultValue: 'TOTAL AMOUNT' })}
                    </Typography>
                </Box>
                {(summary.amounts || []).map((a) => (
                    <Box key={a.currency} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.2, gap: 3 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                            {a.currency}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>
                            {a.symbol}{a.formatted}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    ) : null;

    const leftContent = (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* 선택된 항목 수 표시 */}
            <CommonSelectionDisplay
                selectedItems={selectedItems}
                customLabel={t('common.itemsSelected', { count: selectedItems.length })}
                customNoItemsLabel={t('common.noItemsSelected')}
            />

            {/* 거래 내역서 (DEBIT NOTE) 발급 버튼 */}
            <CommonIconButton
                onClick={onDebitNoteExport}
                disabled={selectedItems.length === 0}
                tooltip={t('invoice.debitNoteButton')}
                variant="primary"
            >
                <DescriptionIcon />
            </CommonIconButton>

            {/* 인보이스 발급 버튼 */}
            <CommonIconButton
                onClick={onInvoiceIssuance}
                disabled={selectedItems.length === 0}
                tooltip={t('invoice.issueButton') || '인보이스 발급'}
                variant="success"
            >
                <ReceiptIcon />
            </CommonIconButton>
        </Box>
    );

    const rightContent = (
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            gap: 1.5,
            width: '100%'
        }}>
            {hasSearched && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', fontWeight: 500, flexShrink: 0 }}
                >
                    {t('common.listLoadCount', {
                        loaded: loadedCount,
                        total: Number.isFinite(totalCount)
                            ? totalCount
                            : (hasMore ? '—' : loadedCount)
                    })}
                </Typography>
            )}
            <AllMyToggle
                checked={showMyOrderOnly}
                onChange={setShowMyOrderOnly}
                t={t}
            />
            <Tooltip
                title={
                    hideInvoicedRows
                        ? t('invoice.hideInvoicedToggle.tooltipOn')
                        : t('invoice.hideInvoicedToggle.tooltipOff')
                }
            >
                <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                    <AllMyToggle
                        checked={hideInvoicedRows}
                        onChange={setHideInvoicedRows}
                        t={t}
                        iconOn={<PendingIcon />}
                        iconOff={<ViewListIcon />}
                    />
                </Box>
            </Tooltip>
            {/* 컬럼 설정 버튼 */}
            <CommonIconButton
                id="settings-button"
                onClick={toggleColumnPanel}
                tooltip="컬럼 설정"
                variant="secondary"
                customStyles={{
                    backgroundColor: columnPanelOpen ? '#3b82f6' : '#ffffff',
                    color: columnPanelOpen ? '#ffffff' : '#6b7280',
                    border: '1px solid #e5e7eb',
                    '&:hover': {
                        backgroundColor: columnPanelOpen ? '#2563eb' : '#f9fafb',
                    }
                }}
            >
                <SettingsIcon />
            </CommonIconButton>
        </Box>
    );

    return (
        <CommonTableControls
            showIconButtons={true}
            leftContent={leftContent}
            centerContent={centerContent}
            rightContent={rightContent}
            spacing={2}
        >
            {/* 컬럼 선택 패널 */}
            <ColumnSelectionPanel
                id="column-selection-panel"
                className={columnPanelOpen ? 'open' : ''}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {t('common.columnSelection') || '컬럼 선택'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={selectAllColumns}
                        >
                            {t('common.selectAll') || '전체 선택'}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={unselectAllColumns}
                        >
                            {t('common.unselectAll') || '전체 해제'}
                        </Button>
                    </Box>
                </Box>

                <ColumnSelectionGrid>
                    {columnsToUse.map((column) => (
                        <ColumnSelectionItem key={column.id}>
                            <Checkbox
                                checked={checkedColumns[column.id] || false}
                                onChange={() => handleCheckColumn(column.id)}
                                size="small"
                            />
                            <Typography variant="body2">
                                {englishLabelById[column.id] || column.label}
                            </Typography>
                        </ColumnSelectionItem>
                    ))}
                </ColumnSelectionGrid>
            </ColumnSelectionPanel>
        </CommonTableControls>
    );
};

export default TableControls; 